import { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Grid,
  LinearProgress,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  IconButton,
  Chip,
  Stack,
  Popover,
  Divider,
  Pagination,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Users,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Crown,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Eye,
  MoreVertical,
  User,
  Briefcase,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  LayoutGrid,
  List,
  BookOpen,
  Heart,
  Home,
  DollarSign,
  Image,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiEndpoints from "../../apiconfig";
// import userManagementConfig from "../../components/UserManagementConfig";

// Full config object

export default function UserManagement() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [state, setState] = useState({
    searchTerm: "",
    premiumFilter: "all",
    isDeletedFilter: "not-deleted",
    isVerifiedFilter: "all",
    activeFilter: "all", // "all" by default
    locationFilter: "all", // "all" by default
    users: [],
    filteredUsers: [],
    totalUsers: 0,
    loading: false,
    isAddDialogOpen: false,
    isEditDialogOpen: false,
    isViewDialogOpen: false,
    isDeleteDialogOpen: false,
    userIdToDelete: null,
    selectedUser: null,
    viewMode: "table",
    viewDialogTab: 0,
  });

  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    phone: "",
    age: "",
    city: "",
    profession: "",
    isPremium: false,
  });

  const [editUser, setEditUser] = useState({
    id: "",
    name: "",
    email: "",
    phone: "",
    age: "",
    city: "",
    profession: "",
    isPremium: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [selectedUserForMenu, setSelectedUserForMenu] = useState(null);

  const fetchUsers = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      const authToken = localStorage.getItem("authToken");

      const response = await fetch(`${apiEndpoints.userDetails}?action=list`, {
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : "",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const data = await response.json();

      if (data.status === "success" || data.users) {
        const users = data.users || [];
        setState((prev) => ({
          ...prev,
          users,
          totalUsers: data.total || users.length,
          loading: false,
          filteredUsers: applyFiltersToData(users, prev),
        }));
      } else {
        throw new Error(data.message || "Failed to fetch users");
      }
    } catch (error) {
      console.error("Fetch Users Error:", error);
      showSnackbar(error.message || "Could not load users", "error");
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });
  const startIndex = (pagination.page - 1) * pagination.limit;
  const paginatedUsers = state.filteredUsers.slice(
    startIndex,
    startIndex + pagination.limit,
  );

  // Helper to apply filters to a dataset
  const applyFiltersToData = (usersData, currentState) => {
    let filtered = [...usersData];

    // Premium / Free
    if (currentState.premiumFilter === "premium")
      filtered = filtered.filter((u) => u.isPremium === 1);
    if (currentState.premiumFilter === "free")
      filtered = filtered.filter((u) => u.isPremium === 0);

    // Deleted / Not Deleted
    if (currentState.isDeletedFilter === "deleted")
      filtered = filtered.filter((u) => u.isDeleted === 1);
    if (currentState.isDeletedFilter === "not-deleted")
      filtered = filtered.filter((u) => u.isDeleted === 0);

    // Verified / Unverified
    if (currentState.isVerifiedFilter === "verified")
      filtered = filtered.filter((u) => u.isVerified === 1);
    if (currentState.isVerifiedFilter === "not-verified")
      filtered = filtered.filter((u) => u.isVerified === 0);

    // Active / Inactive
    if (currentState.activeFilter === "active")
      filtered = filtered.filter((u) => u.isActive === 1 || u.isVerified === 1);
    if (currentState.activeFilter === "inactive")
      filtered = filtered.filter((u) => u.isActive === 0 && u.isVerified === 0);

    // Location
    if (currentState.locationFilter && currentState.locationFilter !== "all")
      filtered = filtered.filter((u) => u.city === currentState.locationFilter);

    // Search
    if (currentState.searchTerm) {
      filtered = filtered.filter((u) => {
        const term = currentState.searchTerm.toLowerCase();
        return (
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term) ||
          u.phone?.toLowerCase().includes(term) ||
          u.user_id?.toLowerCase().includes(term) ||
          u.city?.toLowerCase().includes(term)
        );
      });
    }

    return filtered;
  };

  // Call this whenever a filter changes
  const applyFilters = () => {
    setState((prev) => ({
      ...prev,
      filteredUsers: applyFiltersToData(prev.users, prev),
    }));
  };

  const statusOptions = [
    "all",
    ...(Array.isArray(state.users)
      ? [...new Set(state.users.map((u) => u.status))]
      : []),
  ];
  const locationOptions = [
    "all",
    ...(Array.isArray(state.users)
      ? [...new Set(state.users.map((u) => u.city))] // ✅ use city
      : []),
  ];
  // Auto-apply filters whenever users or filters change
  const {
    users,
    searchTerm,
    premiumFilter,
    isDeletedFilter,
    isVerifiedFilter,
    activeFilter,
    locationFilter,
  } = state;

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      filteredUsers: applyFiltersToData(users, {
        searchTerm,
        premiumFilter,
        isDeletedFilter,
        isVerifiedFilter,
        activeFilter,
        locationFilter,
      }),
    }));
  }, [
    users,
    searchTerm,
    premiumFilter,
    isDeletedFilter,
    isVerifiedFilter,
    activeFilter,
    locationFilter,
  ]);

  useEffect(() => {
    fetchUsers(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  // const handleSearchChange = (value) => {
  //   setState((prev) => ({ ...prev, searchTerm: value }));
  //   setTimeout(applyFilters, 100);
  // };

  // const handleFilterChange = (filterType, value) => {
  //   setState((prev) => ({ ...prev, [filterType]: value }));
  //   setTimeout(applyFilters, 100);
  // };

  // const togglePremium = (userId) => {
  //   setState((prev) => ({
  //     ...prev,
  //     users: prev.users.map((user) =>
  //       user.id === userId ? { ...user, isPremium: !user.isPremium } : user
  //     ),
  //     filteredUsers: prev.filteredUsers.map((user) =>
  //       user.id === userId ? { ...user, isPremium: !user.isPremium } : user
  //     ),
  //   }));
  // };

  const deleteUser = async (userId) => {
    try {
      const formData = new FormData();
      formData.append("action", "delete");
      formData.append("id", userId);

      const response = await fetch(apiEndpoints.userDetails, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.status === "success") {
        showSnackbar("Deleted user successfully ! ", "success");
        setState((prev) => ({
          ...prev,
          isDeleteDialogOpen: false,
          userIdToDelete: null,
          users: prev.users.map((u) =>
            u.id === userId ? { ...u, isDeleted: 1 } : u,
          ),
          filteredUsers: prev.filteredUsers.map((u) =>
            u.id === userId ? { ...u, isDeleted: 1 } : u,
          ),
        }));
      } else {
        showSnackbar("Failed to delete: " + result.message, "error");
      }
    } catch (error) {
      console.error("Delete error:", error);
      showSnackbar("Something went wrong during deletion", "error");
    }
  };

  const addUser = () => {
    const user = {
      id: Date.now(),
      ...newUser,
      age: Number(newUser.age),
      avatar: "/diverse-user-avatars.png",
      status: "active",
      createdOn: new Date().toISOString(),
      lastActive: new Date().toISOString(),
      profileCompletion: 50,
    };

    setState((prev) => ({
      ...prev,
      users: [...prev.users, user],
      filteredUsers: [...prev.filteredUsers, user],
      isAddDialogOpen: false,
    }));

    setNewUser({
      name: "",
      email: "",
      phone: "",
      age: "",
      city: "",
      profession: "",
      isPremium: false,
    });
  };

  const updateUser = () => {
    setState((prev) => ({
      ...prev,
      users: prev.users.map((user) =>
        user.id === editUser.id
          ? { ...user, ...editUser, age: Number(editUser.age) }
          : user,
      ),
      filteredUsers: prev.filteredUsers.map((user) =>
        user.id === editUser.id
          ? { ...user, ...editUser, age: Number(editUser.age) }
          : user,
      ),
      isEditDialogOpen: false,
    }));

    setEditUser({
      id: "",
      name: "",
      email: "",
      phone: "",
      age: "",
      city: "",
      profession: "",
      isPremium: false,
    });
  };

  const handleEditClick = (user) => {
    // setEditUser({
    //   id: user.id,
    //   name: user.name,
    //   email: user.email,
    //   phone: user.phone,
    //   age: user.age.toString(),
    //   location: user.location,
    //   profession: user.profession,
    //   isPremium: user.isPremium,
    // });
    // setState((prev) => ({
    //   ...prev,
    //   isEditDialogOpen: true,
    // }));
    // setMobileMenuAnchor(null);

    navigate("/dashboard/users/edit-profile", { state: { user } });
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "inactive":
      default:
        return "default";
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatDateTime = (dateString) =>
    new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const mapUserStatus = (user) => {
    if (user.isVerified === 1 || user.isActive === 1) return "active";
    if (user.isActive === 0) return "inactive";
    return "pending";
  };

  return (
    <Box
      sx={{ width: "100%", pb: 4 }}
      display="flex"
      flexDirection="column"
      gap={4}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all registered users
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack
            direction="row"
            spacing={1}
            sx={{ bgcolor: "rgba(0,0,0,0.05)", p: 0.5, borderRadius: 2 }}
          >
            <IconButton
              size="small"
              onClick={() =>
                setState((prev) => ({ ...prev, viewMode: "table" }))
              }
              sx={{
                bgcolor: state.viewMode === "table" ? "white" : "transparent",
                boxShadow: state.viewMode === "table" ? 1 : 0,
                color: "black",
              }}
            >
              <List size={18} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() =>
                setState((prev) => ({ ...prev, viewMode: "grid" }))
              }
              sx={{
                bgcolor: state.viewMode === "grid" ? "white" : "transparent",
                boxShadow: state.viewMode === "grid" ? 1 : 0,
                color: "black",
              }}
            >
              <LayoutGrid size={18} />
            </IconButton>
          </Stack>
          <Button
            variant="contained"
            startIcon={<Plus />}
            size={isMobile ? "small" : "medium"}
            onClick={() => navigate("/dashboard/users/add-user")}
            sx={{
              backgroundColor: "#ffe599",
              color: "black",
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#ffe599",
              },
            }}
          >
            {isMobile ? "Add" : "Add User"}
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Card sx={{ p: 2, borderRadius: "12px" }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Field */}
          <Grid item xs={12} md={4} width={isMobile ? "100%" : "auto"}>
            <TextField
              fullWidth
              placeholder="Search users..."
              variant="outlined"
              size="small"
              value={state.searchTerm}
              onChange={(e) =>
                setState((prev) => ({ ...prev, searchTerm: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <Search style={{ marginRight: 8, fontSize: 18 }} />
                ),
              }}
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={6} sm={4} md={2} width={isMobile ? "100%" : "200px"}>
            <TextField
              select
              fullWidth
              label="Status"
              size="small"
              value={state.activeFilter}
              onChange={(e) =>
                setState((prev) => ({ ...prev, activeFilter: e.target.value }))
              }
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Grid>

          {/* Location Filter */}
          <Grid item xs={6} sm={4} md={2} width={isMobile ? "100%" : "200px"}>
            <TextField
              select
              fullWidth
              label="Location"
              size="small"
              value={state.locationFilter}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  locationFilter: e.target.value,
                }))
              }
            >
              {locationOptions
                .filter((loc) => loc)
                .map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Box display="flex" gap={1} flexWrap="wrap">
            {/* Premium */}
            <Chip
              label="Premium"
              variant={
                state.premiumFilter === "premium" ? "filled" : "outlined"
              }
              color="primary"
              onClick={() =>
                setState((prev) => ({ ...prev, premiumFilter: "premium" }))
              }
            />

            {/* Free */}
            <Chip
              label="Free"
              variant={state.premiumFilter === "free" ? "filled" : "outlined"}
              color="default"
              onClick={() =>
                setState((prev) => ({ ...prev, premiumFilter: "free" }))
              }
            />

            {/* Deleted */}
            <Chip
              label="Deleted"
              variant={
                state.isDeletedFilter === "deleted" ? "filled" : "outlined"
              }
              color="error"
              onClick={() =>
                setState((prev) => ({ ...prev, isDeletedFilter: "deleted" }))
              }
            />

            {/* Not Deleted */}
            <Chip
              label="Not Deleted"
              variant={
                state.isDeletedFilter === "not-deleted" ? "filled" : "outlined"
              }
              color="default"
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  isDeletedFilter: "not-deleted",
                }))
              }
            />

            {/* Approved */}
            <Chip
              label="Approved"
              variant={
                state.isVerifiedFilter === "verified" ? "filled" : "outlined"
              }
              color="success"
              onClick={() =>
                setState((prev) => ({ ...prev, isVerifiedFilter: "verified" }))
              }
            />

            {/* Unapproved */}
            <Chip
              label="Unapproved"
              variant={
                state.isVerifiedFilter === "not-verified"
                  ? "filled"
                  : "outlined"
              }
              color="default"
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  isVerifiedFilter: "not-verified",
                }))
              }
            />
            {/* Active */}
            <Chip
              label="Active"
              variant={state.activeFilter === "active" ? "filled" : "outlined"}
              color="primary"
              onClick={() =>
                setState((prev) => ({ ...prev, activeFilter: "active" }))
              }
            />

            {/* Inactive */}
            <Chip
              label="Inactive"
              variant={
                state.activeFilter === "inactive" ? "filled" : "outlined"
              }
              color="default"
              onClick={() =>
                setState((prev) => ({ ...prev, activeFilter: "inactive" }))
              }
            />

            {/* Optional: Reset Filters */}
            <Chip
              label="Clear Filters"
              variant="outlined"
              color="secondary"
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  searchTerm: "",
                  premiumFilter: "all",
                  isDeletedFilter: "not-deleted",
                  isVerifiedFilter: "all",
                  activeFilter: "all",
                  locationFilter: "all",
                }))
              }
            />
          </Box>

          {/* Apply Filters Button */}
        </Grid>
      </Card>

      {/* Users Table/Cards */}
      {/* Users Table/Cards */}
      {state.viewMode === "grid" || isMobile ? (
        // Grid View / Mobile Card View
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
            lg: "1fr 1fr 1fr 1fr",
          }}
          gap={2}
        >
          {Array.isArray(paginatedUsers) &&
            paginatedUsers.map((user) => (
              <Card
                key={user.id}
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    selectedUser: user,
                    isViewDialogOpen: true,
                  }))
                }
              >
                <Box p={2}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Avatar
                      src={`${apiEndpoints.image}${user.profile_photo}`}
                      alt={user.name}
                      sx={{ width: 50, height: 50 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography fontWeight={700} noWrap>
                        {user.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        noWrap
                        sx={{ display: "block" }}
                      >
                        ID: {user.user_id || user.id}
                      </Typography>
                    </Box>
                    <Chip
                      label={mapUserStatus(user)}
                      size="small"
                      color={getStatusBadgeColor(mapUserStatus(user))}
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.6rem" }}
                    />
                  </Box>

                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Mail size={14} color="#666" />
                      <Typography variant="caption" noWrap>
                        {user.email}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone size={14} color="#666" />
                      <Typography variant="caption">{user.phone}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <MapPin size={14} color="#666" />
                      <Typography variant="caption" noWrap>
                        {user.city || "N/A"}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{
                          color:
                            user.plan_id === 2
                              ? "#C0C0C0"
                              : user.plan_id === 3
                                ? "#FFD700"
                                : user.plan_id === 4
                                  ? "#b9f2ff"
                                  : "#666",
                        }}
                      >
                        <Crown
                          size={14}
                          fill={user.plan_id >= 3 ? "currentColor" : "none"}
                        />
                        <Typography variant="caption" fontWeight={700}>
                          {user.plan_id === 1
                            ? "Free"
                            : user.plan_id === 2
                              ? "Silver"
                              : user.plan_id === 3
                                ? "Gold"
                                : user.plan_id === 4
                                  ? "Diamond"
                                  : user.isPremium
                                    ? "Premium"
                                    : "Free"}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {user.req_counter || 0} Pts
                      </Typography>
                    </Box>

                    <Divider sx={{ mt: 1.5, mb: 1, borderStyle: "dashed" }} />

                    <Box
                      display="flex"
                      justifyContent="space-around"
                      alignItems="center"
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setState((prev) => ({
                            ...prev,
                            selectedUser: user,
                            isViewDialogOpen: true,
                          }));
                        }}
                        sx={{
                          color: "black",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.05)" },
                        }}
                      >
                        <Eye size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(user);
                        }}
                        sx={{
                          color: "black",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.05)" },
                        }}
                      >
                        <Edit size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setState((prev) => ({
                            ...prev,
                            isDeleteDialogOpen: true,
                            userIdToDelete: user.id,
                          }));
                        }}
                        sx={{
                          color: "#d32f2f",
                          "&:hover": { bgcolor: "rgba(211,47,47,0.05)" },
                        }}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </Box>
                  </Stack>
                </Box>
              </Card>
            ))}
        </Box>
      ) : (
        // Desktop/Tablet Table View
        <Card sx={{ borderRadius: "12px", overflow: "auto" }}>
          <TableContainer>
            <Table sx={{ minWidth: isTablet ? 800 : 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>User</TableCell>
                  {!isTablet && <TableCell>Contact</TableCell>}
                  <TableCell>Details</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(paginatedUsers) &&
                  paginatedUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {user.user_id || user.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            src={`${apiEndpoints.image}${user.profile_photo}`}
                            alt={user.name}
                          />
                          <Box>
                            <Typography fontWeight={600}>
                              {user.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {user.profession}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {!isTablet && (
                        <TableCell>
                          <Box display="flex" flexDirection="column">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Mail size={15} />
                              <Typography variant="body2">
                                {user.email}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Phone size={15} />
                              <Typography variant="body2">
                                {user.phone}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Calendar size={14} />
                            <Typography variant="caption">
                              {user.age} yrs • {user.city || "N/A"}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              sx={{
                                color:
                                  user.plan_id === 2
                                    ? "#C0C0C0"
                                    : user.plan_id === 3
                                      ? "#FFD700"
                                      : user.plan_id === 4
                                        ? "#b9f2ff"
                                        : "#666",
                              }}
                            >
                              <Crown
                                size={14}
                                fill={
                                  user.plan_id >= 3 ? "currentColor" : "none"
                                }
                              />
                              <Typography
                                variant="caption"
                                fontWeight={user.isPremium ? 700 : 400}
                              >
                                {user.plan_id === 1
                                  ? "Free"
                                  : user.plan_id === 2
                                    ? "Silver"
                                    : user.plan_id === 3
                                      ? "Gold"
                                      : user.plan_id === 4
                                        ? "Diamond"
                                        : user.isPremium
                                          ? "Premium"
                                          : "Free"}
                              </Typography>
                            </Box>
                            <Chip
                              label={`${user.req_counter || 0} Pts`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                bgcolor: "#f5f5f5",
                              }}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={mapUserStatus(user)}
                          size="small"
                          color={getStatusBadgeColor(mapUserStatus(user))}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(user.createdOn)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              setState((prev) => ({
                                ...prev,
                                selectedUser: user,
                                isViewDialogOpen: true,
                              }))
                            }
                            // color="#000000"
                          >
                            <Eye size={16} color={"#000000"} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(user)}
                          >
                            <Edit size={16} color={"#000000"} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setState((prev) => ({
                                ...prev,
                                isDeleteDialogOpen: true,
                                userIdToDelete: user.id,
                              }));
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
      <Box display="flex" justifyContent="center">
        <Pagination
          count={Math.ceil(state.filteredUsers.length / pagination.limit)}
          page={pagination.page}
          onChange={(e, value) =>
            setPagination((prev) => ({ ...prev, page: value }))
          }
        />
      </Box>

      {/* Mobile Menu Popover */}
      <Popover
        open={Boolean(mobileMenuAnchor)}
        anchorEl={mobileMenuAnchor}
        onClose={() => setMobileMenuAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box p={1}>
          <Stack direction="column" spacing={1}>
            <Button
              size="small"
              startIcon={<Eye size={16} />}
              onClick={() => {
                setState((prev) => ({
                  ...prev,
                  selectedUser: selectedUserForMenu,
                  isViewDialogOpen: true,
                }));
                setMobileMenuAnchor(null);
              }}
              sx={{ color: "#000000" }}
            >
              View
            </Button>
            <Button
              size="small"
              startIcon={<Edit size={16} />}
              onClick={() => handleEditClick(selectedUserForMenu)}
              sx={{ color: "#000000" }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<Trash2 size={16} />}
              color="error"
              onClick={() => {
                setState((prev) => ({
                  ...prev,
                  isDeleteDialogOpen: true,
                  userIdToDelete: selectedUserForMenu.id,
                }));
                setMobileMenuAnchor(null);
              }}
            >
              Delete
            </Button>
          </Stack>
        </Box>
      </Popover>

      {/* View User Dialog */}
      <Dialog
        open={state.isViewDialogOpen}
        onClose={() =>
          setState((prev) => ({ ...prev, isViewDialogOpen: false }))
        }
        maxWidth="md"
        fullWidth
      >
        {state.selectedUser && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={`${apiEndpoints.image}${state.selectedUser.profile_photo}`}
                  alt={state.selectedUser.name}
                  sx={{ width: 60, height: 60 }}
                />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {state.selectedUser.name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      ID: {state.selectedUser.user_id}
                    </Typography>
                    <Chip
                      label={state.selectedUser.isPremium ? "Premium" : "Free"}
                      size="small"
                      variant="outlined"
                      color={
                        state.selectedUser.isPremium ? "success" : "default"
                      }
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  </Box>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent
              dividers
              sx={{ maxHeight: "80vh", overflowY: "auto", px: 2 }}
            >
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={state.viewDialogTab}
                  onChange={(e, newValue) =>
                    setState((prev) => ({ ...prev, viewDialogTab: newValue }))
                  }
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      fontFamily: "Montserrat, sans-serif",
                      minHeight: 48,
                    },
                    "& .Mui-selected": {
                      color: "#b8860b !important",
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#b8860b",
                    },
                  }}
                >
                  <Tab
                    label="System"
                    icon={<Info size={16} />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Personal"
                    icon={<User size={16} />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Family"
                    icon={<Users size={16} />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Preferences"
                    icon={<Heart size={16} />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Uploads"
                    icon={<Image size={16} />}
                    iconPosition="start"
                  />
                </Tabs>
              </Box>

              <Box sx={{ p: 2 }}>
                {[
                  {
                    title: "System Information",
                    fields: [
                      ["Member ID", "user_id"],
                      ["Points", "req_counter"],
                      ["Premium Status", "isPremium"],
                      ["Verified", "isVerified"],
                      ["Member Since", "createdOn"],
                    ],
                  },
                  {
                    title: "Personal Information",
                    fields: [
                      ["Name", "name"],
                      ["Email", "email"],
                      ["Phone", "phone"],
                      ["Age", "age"],
                      ["DOB", "dob"],
                      ["Birth Place", "birth_place"],
                      ["Birth Time", "birth_time"],
                      ["Gender", "gender"],
                      ["Education", "education"],
                      ["Blood Group", "blood_group"],
                      ["Address", "address"],
                      ["Country", "country"],
                      ["State", "state"],
                      ["City", "city"],
                      ["Marital Status", "marital_status"],
                      ["Profession", "profession"],
                      ["Annual Income", "annual_income"],
                      ["Religion", "religion"],
                      ["Caste", "caste"],
                      ["Zodiac Sign", "zodiac_sign"],
                      ["Zodiac Star", "zodiac_star"],
                      ["About Me", "about_me"],
                    ],
                  },
                  {
                    title: "Family Information",
                    fields: [
                      ["Family Type", "family_type"],
                      ["Father Name", "father_name"],
                      ["Mother Name", "mother_name"],
                      ["Father Occupation", "father_occupation"],
                      ["Mother Occupation", "mother_occupation"],
                      ["No. of Brothers", "no_of_brothers"],
                      ["No. of Sisters", "no_of_sisters"],
                      ["Family Status", "family_status"],
                      ["About Family", "about_family"],
                    ],
                  },
                  {
                    title: "Partner Preferences",
                    fields: [
                      ["Religion", "pref_religion"],
                      ["Country", "pref_country"],
                      ["State", "pref_state"],
                      ["City", "pref_city"],
                      ["Marital Status", "pref_marital_status"],
                      ["Blood Group", "pref_blood_group"],
                      ["Caste", "pref_caste"],
                      ["Zodiac Sign", "pref_zodiac_sign"],
                      ["Zodiac Star", "pref_zodiac_star"],
                      ["Education", "pref_education"],
                      ["Profession", "pref_profession"],
                      ["Annual Income", "pref_annual_income"],
                    ],
                  },
                ].map((section, index) => (
                  <div
                    role="tabpanel"
                    hidden={state.viewDialogTab !== index}
                    key={index}
                  >
                    {state.viewDialogTab === index && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {section.fields.map(([label, key]) => (
                          <Grid item xs={12} sm={6} md={4} key={key}>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ fontFamily: "Montserrat, sans-serif" }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{
                                  fontFamily: "Montserrat, sans-serif",
                                  color: "#000",
                                }}
                              >
                                {key === "isPremium"
                                  ? state.selectedUser[key]
                                    ? "Premium"
                                    : "Free"
                                  : key === "isVerified"
                                    ? state.selectedUser[key]
                                      ? "Yes"
                                      : "No"
                                    : key === "createdOn"
                                      ? formatDate(state.selectedUser[key])
                                      : state.selectedUser[key] || "N/A"}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </div>
                ))}

                {/* Uploads Tab Content (Index 4) */}
                <div role="tabpanel" hidden={state.viewDialogTab !== 4}>
                  {state.viewDialogTab === 4 && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {[
                        ["Profile Photo", "profile_photo"],
                        ["Profile Photo 1", "profile_photo1"],
                        ["Profile Photo 2", "profile_photo2"],
                        ["Profile Photo 3", "profile_photo3"],
                        ["Horoscope", "horoscope"],
                        ["ID Proof", "ID_proof"],
                      ].map(([label, key]) => (
                        <Grid item xs={6} sm={4} md={3} key={key}>
                          <Box
                            sx={{
                              borderRadius: 2,
                              overflow: "hidden",
                              cursor: state.selectedUser[key]
                                ? "pointer"
                                : "default",
                            }}
                            onClick={() => {
                              if (state.selectedUser[key]) {
                                window.open(
                                  `${apiEndpoints.image}${state.selectedUser[key]}`,
                                  "_blank",
                                );
                              }
                            }}
                          >
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              gutterBottom
                              sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                fontFamily: "Montserrat, sans-serif",
                              }}
                            >
                              {label}
                            </Typography>
                            {state.selectedUser[key] ? (
                              <Box
                                component="img"
                                src={`${apiEndpoints.image}${state.selectedUser[key]}`}
                                alt={label}
                                sx={{
                                  width: "100%",
                                  height: 120,
                                  objectFit: "cover",
                                  borderRadius: 1,
                                  border: "1px solid #eee",
                                  transition: "transform 0.2s",
                                  "&:hover": { transform: "scale(1.02)" },
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 120,
                                  bgcolor: "#f9f9f9",
                                  borderRadius: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "1px dashed #ccc",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  N/A
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </div>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    isViewDialogOpen: false,
                    viewDialogTab: 0,
                  }))
                }
                sx={{ color: "black" }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={state.isAddDialogOpen}
        onClose={() =>
          setState((prev) => ({ ...prev, isAddDialogOpen: false }))
        }
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} width={isMobile ? "100%" : "48%"}>
              <TextField
                fullWidth
                label="Name"
                value={newUser.name}
                onChange={(e) =>
                  setNewUser((prev) => ({ ...prev, name: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} width={isMobile ? "100%" : "48%"}>
              <TextField
                fullWidth
                label="Email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser((prev) => ({ ...prev, email: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} width={isMobile ? "100%" : "48%"}>
              <TextField
                fullWidth
                label="Phone"
                value={newUser.phone}
                onChange={(e) =>
                  setNewUser((prev) => ({ ...prev, phone: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} width={isMobile ? "100%" : "48%"}>
              <TextField
                fullWidth
                type="number"
                label="Age"
                value={newUser.age}
                onChange={(e) =>
                  setNewUser((prev) => ({ ...prev, age: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} width={isMobile ? "100%" : "48%"}>
              <TextField
                fullWidth
                label="Location"
                value={newUser.city}
                onChange={(e) =>
                  setNewUser((prev) => ({ ...prev, city: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} width={isMobile ? "100%" : "48%"}>
              <TextField
                fullWidth
                label="Profession"
                value={newUser.profession}
                onChange={(e) =>
                  setNewUser((prev) => ({
                    ...prev,
                    profession: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} width={isMobile ? "100%" : "48%"}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newUser.isPremium}
                    onChange={(e) =>
                      setNewUser((prev) => ({
                        ...prev,
                        isPremium: e.target.checked,
                      }))
                    }
                    // color="#000000"
                  />
                }
                label="Premium User"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setState((prev) => ({ ...prev, isAddDialogOpen: false }))
            }
            sx={{ color: "black", borderColor: "black" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={addUser}
            sx={{ backgroundColor: "black", color: "white", borderRadius: 2 }}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={state.isEditDialogOpen}
        onClose={() =>
          setState((prev) => ({ ...prev, isEditDialogOpen: false }))
        }
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={editUser.name}
                onChange={(e) =>
                  setEditUser((prev) => ({ ...prev, name: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={editUser.email}
                onChange={(e) =>
                  setEditUser((prev) => ({ ...prev, email: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={editUser.phone}
                onChange={(e) =>
                  setEditUser((prev) => ({ ...prev, phone: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Age"
                value={editUser.age}
                onChange={(e) =>
                  setEditUser((prev) => ({ ...prev, age: e.target.value }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={editUser.city}
                onChange={(e) =>
                  setEditUser((prev) => ({
                    ...prev,
                    city: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Profession"
                value={editUser.profession}
                onChange={(e) =>
                  setEditUser((prev) => ({
                    ...prev,
                    profession: e.target.value,
                  }))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000000",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editUser.isPremium}
                    onChange={(e) =>
                      setEditUser((prev) => ({
                        ...prev,
                        isPremium: e.target.checked,
                      }))
                    }
                    // color="#000000"
                  />
                }
                label="Premium User"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setState((prev) => ({ ...prev, isEditDialogOpen: false }))
            }
            sx={{ color: "#000000" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={updateUser}
            sx={{
              backgroundColor: "#000000",
              color: "white",
              borderRadius: 2,
              "&:hover": { backgroundColor: "#000000" },
            }}
          >
            Update User
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiSnackbar-root": {
            top: "80px",
            right: "24px",
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          //variant="filled"
          sx={{
            width: "100%",
            fontFamily: "'Montserrat', sans-serif",
            alignItems: "center",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={state.isDeleteDialogOpen}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            isDeleteDialogOpen: false,
            userIdToDelete: null,
          }))
        }
        disableRestoreFocus
        PaperProps={{
          sx: { borderRadius: "16px", p: 1, maxWidth: "400px" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            color: "error.main",
            fontWeight: 700,
          }}
        >
          <AlertTriangle size={24} />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ mb: 2, fontWeight: 500, color: "text.primary" }}
          >
            Are you sure you want to permanently delete this user?
          </DialogContentText>
          <Typography variant="body2" color="text.secondary">
            This action will move the user to the deleted list and restrict
            their access.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() =>
              setState((prev) => ({
                ...prev,
                isDeleteDialogOpen: false,
                userIdToDelete: null,
              }))
            }
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteUser(state.userIdToDelete)}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "8px",
              fontWeight: 600,
              px: 3,
              boxShadow: "none",
              backgroundColor: theme.palette.error.main,
              "&:hover": {
                backgroundColor: theme.palette.error.main,
                boxShadow: "none",
              },
            }}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
